import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, CheckCircle2, Calendar, Edit, X, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useReferral,
  useAcceptReferral,
  useUpdateReferral,
  useAssignReferral,
} from '@/hooks/useReferrals';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { ReferralStatusBadge } from './Referrals';
import { ReferralFilesTab } from '@/components/referrals/ReferralFilesTab';
import { ReferralSagsakterTab } from '@/components/referrals/ReferralSagsakterTab';

export function ReferralDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: referral, isLoading, error } = useReferral(id);
  const { data: members } = useClinicMembers(referral?.clinic_id);
  const acceptReferral = useAcceptReferral();
  const updateReferral = useUpdateReferral();
  const assignReferral = useAssignReferral();

  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Administrative fields
  const [secretaryName, setSecretaryName] = useState('');
  const [psychologistName, setPsychologistName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [caseNumber, setCaseNumber] = useState('');

  // Active tab
  type TabType = 'sagsakter' | 'files' | 'lpr' | 'assignment';
  const [activeTab, setActiveTab] = useState<TabType>('sagsakter');

  useEffect(() => {
    if (!referral) return;
    setSecretaryName(referral.secretary_name ?? '');
    setPsychologistName(referral.psychologist_name ?? '');
    setIdNumber(referral.id_number ?? '');
    setCaseNumber(referral.case_number ?? '');
  }, [referral]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('referrals.error_not_found')} />
      </div>
    );
  }

  async function handleAccept() {
    if (!referral) return;
    if (!confirm(t('referrals.confirm_accept'))) return;
    try {
      const result = await acceptReferral.mutateAsync({ referral_id: referral.id });
      toast.success(t('referrals.toast_accepted'));
      navigate(`/patients/${result.patient_id}`);
    } catch (err) {
      console.error('accept referral failed', err);
      toast.error(t('referrals.accept_error'));
    }
  }

  async function handleDecline() {
    if (!referral) return;
    if (!confirm(t('referrals.confirm_decline'))) return;
    try {
      await updateReferral.mutateAsync({ referral_id: referral.id, status: 'declined' });
      toast.success(t('referrals.toast_declined'));
    } catch (err) {
      console.error('decline referral failed', err);
      toast.error(t('referrals.decline_error'));
    }
  }

  async function handleAssign(clinicianId: string | null) {
    if (!referral) return;
    try {
      await assignReferral.mutateAsync({
        referral_id: referral.id,
        clinician_id: clinicianId,
      });
      toast.success(t('referrals.toast_assigned'));
    } catch (err) {
      console.error('assign referral failed', err);
      toast.error(t('referrals.assign_error'));
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!referral) return;

    try {
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        secretary_name: secretaryName.trim() || undefined,
        psychologist_name: psychologistName.trim() || undefined,
        id_number: idNumber.trim() || undefined,
        case_number: caseNumber.trim() || undefined,
      });
      toast.success(t('referrals.toast_updated'));
      setEditing(false);
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
      setSubmitError(t('referrals.update_error'));
    }
  }

  const pending =
    acceptReferral.isPending || updateReferral.isPending || assignReferral.isPending;

  const isNew = referral.status === 'new';
  const isAccepted = referral.status === 'accepted';

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          <button
            type="button"
            onClick={() => navigate('/henvisninger')}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('referrals.back_to_list')}
          </button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
                {referral.first_name} {referral.last_name}
              </h2>
              <ReferralStatusBadge status={referral.status} />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Info-indsamling:
                </span>
                <InfoCollectionProgress referral={referral} />
              </div>
              <div className="flex flex-wrap gap-2">
                {!editing && (
                  <Button onClick={() => setEditing(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    {t('common.edit')}
                  </Button>
                )}
                {editing && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditing(false);
                      setSubmitError(null);
                      // Reset to original values
                      if (referral) {
                        setSecretaryName(referral.secretary_name ?? '');
                        setPsychologistName(referral.psychologist_name ?? '');
                        setIdNumber(referral.id_number ?? '');
                        setCaseNumber(referral.case_number ?? '');
                      }
                    }}
                    disabled={updateReferral.isPending}
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('common.cancel')}
                  </Button>
                )}
                {isNew && (
                  <>
                    <Button onClick={handleAccept} disabled={pending}>
                      {acceptReferral.isPending ? t('common.saving') : t('referrals.accept')}
                    </Button>
                    <Button variant="secondary" onClick={handleDecline} disabled={pending}>
                      {t('referrals.decline')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('referrals.received_label')}{' '}
            {new Date(referral.received_at).toLocaleDateString('da-DK')}
            {isAccepted && referral.patient_id && (
              <>
                {' · '}
                <Link
                  to={`/patients/${referral.patient_id}`}
                  className="text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  {t('referrals.view_patient')}
                </Link>
              </>
            )}
          </p>

          {submitError && (
            <div className="mt-6">
              <ErrorAlert message={submitError} />
            </div>
          )}

          {/* Info slots at the top */}
          {editing ? (
            <form onSubmit={onSubmit} className="mt-6">
              <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
                <div className="grid gap-px bg-gray-100 dark:bg-gray-700/50 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Patient info */}
                  <InfoCell
                    label={t('referrals.section_patient')}
                    value={
                      <div className="space-y-1">
                        <div className="text-sm font-medium">{referral.first_name} {referral.last_name}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {referral.phone ?? '—'}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {referral.email ?? '—'}
                        </div>
                      </div>
                    }
                  />
                  {/* Referrer info */}
                  <InfoCell
                    label={t('referrals.section_referrer')}
                    value={
                      <div className="space-y-1">
                        <div className="text-sm font-medium">{referral.referrer_name ?? '—'}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {referral.referrer_type
                            ? t(`referrals.referrer_type_${referral.referrer_type}`)
                            : '—'}
                        </div>
                        {referral.referrer_reference && (
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            Ref: {referral.referrer_reference}
                          </div>
                        )}
                      </div>
                    }
                  />
                  {/* Details stub - not editable in edit mode */}
                  <InfoCell
                    label={t('referrals.section_details')}
                    value={
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {referral.reason || referral.notes ? 'Se faner nedenfor' : '—'}
                      </div>
                    }
                  />
                  {/* Administrative info stub during edit */}
                  <InfoCell
                    label="Administrativ information"
                    value={
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Redigerer...
                      </div>
                    }
                  />
                </div>

                {/* Edit form in expanded section */}
                <div className="space-y-4 p-6">
                  <h3 className="text-base font-medium text-gray-900 dark:text-white">
                    Rediger administrativ information
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="Sekretær"
                      value={secretaryName}
                      onChange={(e) => setSecretaryName(e.target.value)}
                    />
                    <Input
                      label="Psykolog"
                      value={psychologistName}
                      onChange={(e) => setPsychologistName(e.target.value)}
                    />
                    <Input
                      label="ID #"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                    />
                    <Input
                      label="Sagsnr."
                      value={caseNumber}
                      onChange={(e) => setCaseNumber(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <Button type="submit" disabled={updateReferral.isPending}>
                      {updateReferral.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('common.saving')}
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {t('common.save')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </section>
            </form>
          ) : (
            <section className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
              <div className="grid gap-px bg-gray-100 dark:bg-gray-700/50 sm:grid-cols-2 lg:grid-cols-4">
                {/* Patient info */}
                <InfoCell
                  label={t('referrals.section_patient')}
                  value={
                    <div className="space-y-1">
                      <div className="text-sm font-medium">{referral.first_name} {referral.last_name}</div>
                      {referral.has_cpr && (
                        <div className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          {t('referrals.cpr_present')}
                        </div>
                      )}
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {referral.phone ?? '—'}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {referral.email ?? '—'}
                      </div>
                    </div>
                  }
                />
                {/* Referrer info */}
                <InfoCell
                  label={t('referrals.section_referrer')}
                  value={
                    <div className="space-y-1">
                      <div className="text-sm font-medium">{referral.referrer_name ?? '—'}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {referral.referrer_type
                          ? t(`referrals.referrer_type_${referral.referrer_type}`)
                          : '—'}
                      </div>
                      {referral.referrer_reference && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Ref: {referral.referrer_reference}
                        </div>
                      )}
                    </div>
                  }
                />
                {/* Details (reason/notes) */}
                <InfoCell
                  label={t('referrals.section_details')}
                  value={
                    <div className="space-y-1">
                      {referral.reason && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                          {referral.reason}
                        </div>
                      )}
                      {referral.notes && (
                        <div className="text-xs text-gray-500 dark:text-gray-500 line-clamp-1">
                          {referral.notes}
                        </div>
                      )}
                      {!referral.reason && !referral.notes && (
                        <div className="text-xs text-gray-400">—</div>
                      )}
                    </div>
                  }
                />
                {/* Administrative info */}
                <InfoCell
                  label="Administrativ information"
                  value={
                    <div className="space-y-1">
                      {referral.secretary_name && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Sekr: {referral.secretary_name}
                        </div>
                      )}
                      {referral.psychologist_name && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Psyk: {referral.psychologist_name}
                        </div>
                      )}
                      {referral.id_number && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          ID: {referral.id_number}
                        </div>
                      )}
                      {referral.case_number && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Sag: {referral.case_number}
                        </div>
                      )}
                      {!referral.secretary_name && !referral.psychologist_name && !referral.id_number && !referral.case_number && (
                        <div className="text-xs text-gray-400">—</div>
                      )}
                    </div>
                  }
                />
              </div>
            </section>
          )}

          {/* Tab Navigation */}
          <div className="mt-4">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-4 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveTab('sagsakter')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'sagsakter'
                        ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                    }
                  `}
                >
                  Sagsakter (NPU)
                </button>
                <button
                  onClick={() => setActiveTab('files')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'files'
                        ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                    }
                  `}
                >
                  {t('referrals.tab_files')}
                </button>
                <button
                  onClick={() => setActiveTab('lpr')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'lpr'
                        ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                    }
                  `}
                >
                  LPR
                </button>
                <button
                  onClick={() => setActiveTab('assignment')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'assignment'
                        ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                    }
                  `}
                >
                  Tildeling
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === 'sagsakter' && <ReferralSagsakterTab referral={referral} />}

              {activeTab === 'files' && <ReferralFilesTab referral={referral} />}

              {activeTab === 'lpr' && (
                <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">LPR</h2>
                </section>
              )}

              {activeTab === 'assignment' && (
                <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    {t('referrals.assignment_title')}
                  </h2>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t('referrals.field_assigned_clinician')}
                    </label>
                    <select
                      value={referral.assigned_clinician_id ?? ''}
                      onChange={(e) => handleAssign(e.target.value || null)}
                      disabled={pending}
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:max-w-xs"
                    >
                      <option value="">{t('referrals.unassigned')}</option>
                      {members?.map((m) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    {referral.assigned_clinician_id && (
                      <div className="mt-4">
                        <Link
                          to="/appointments"
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                        >
                          <Calendar className="h-4 w-4" />
                          {t('referrals.view_calendar')}
                        </Link>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-white px-4 py-3 dark:bg-card-dark">
      <div className="mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </span>
      </div>
      <div className="text-sm text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

function InfoCollectionProgress({ referral }: { referral: { consent_collected: boolean; info_letter_sent: boolean; agreement_made: boolean; payment_arranged: boolean } }) {
  const fields = [
    referral.consent_collected,
    referral.info_letter_sent,
    referral.agreement_made,
    referral.payment_arranged,
  ];
  
  const collected = fields.filter(Boolean).length;
  const total = 4;

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
        {collected}/{total}
      </span>
      <div className="flex gap-0.5">
        {fields.map((isChecked, i) => (
          <div
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${
              isChecked
                ? 'bg-emerald-500 dark:bg-emerald-400'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
